import React, { Fragment, useState, useContext } from "react";
import clsx from "clsx";
import { Theme, makeStyles } from "@material-ui/core/styles";
import {
  Typography,
  Button,
  ButtonGroup,
  Popover,
  List,
  ListItemIcon,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { ArrowDropDown } from "@material-ui/icons";
import { ChromePicker } from "react-color";
import {
  RectangleDefault,
  CircleDefault,
  LineDefault,
} from "../../material/icons/equipemnt";
import { DesignContext } from "../../context/design";

const useStyles = makeStyles((theme: Theme) => ({
  default: { color: theme.palette.secondary.main },
  active: { color: `${theme.palette.primary.main} !important` },
  spaceInGroup: {
    marginLeft: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  flexMiddle: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  select: { height: theme.spacing(4) },
  btnSelect: {
    boxShadow: "unset",
    padding: theme.spacing(0.3),
    width: theme.spacing(4),
    height: theme.spacing(4),
    fontFamily: ["Montserrat-Bold", "Kanit-Bold"].join(","),
    minWidth: "unset",
  },
}));

const shapeList = [
  "anno-shape-rectangle",
  "anno-shape-circle",
  "anno-shape-stroke",
];

interface IShape {
  shape: "anno-shape-rectangle" | "anno-shape-circle" | "anno-shape-stroke";
}

const DrawingShape = () => {
  const classes = useStyles();
  const [shapepickEl, setshapepickEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [colorpickEl, setcolorpickEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [shapepick, setshapepick] = useState<IShape["shape"]>(
    "anno-shape-rectangle"
  );

  const { event, setevent, map } = useContext(DesignContext);
  const { type, feature } = event;

  const renderShape = (shape: IShape["shape"]) => {
    return shape === "anno-shape-rectangle" ? (
      <RectangleDefault
        className={clsx(
          classes.default,
          Boolean(type?.includes("anno-shape") && !feature) && classes.active
        )}
        style={{
          strokeWidth: 4,
          stroke: Boolean(type?.includes("anno-shape") && !feature)
            ? "#ff5722"
            : Boolean(type?.includes("anno-shape") && feature)
            ? "rgba(0, 0, 0, 0.26)"
            : "rgba(0, 0, 0, 1)",
          fill: "none",
        }}
      />
    ) : shape === "anno-shape-circle" ? (
      <CircleDefault
        className={clsx(classes.default)}
        style={{
          strokeWidth: 4,
          stroke: Boolean(type?.includes("anno-shape") && !feature)
            ? "#ff5722"
            : Boolean(type?.includes("anno-shape") && feature)
            ? "rgba(0, 0, 0, 0.26)"
            : "rgba(0, 0, 0, 1)",
          fill: "none",
        }}
      />
    ) : (
      <LineDefault
        className={clsx(classes.default)}
        style={{
          strokeWidth: 4,
          stroke: Boolean(type?.includes("anno-shape") && !feature)
            ? "#ff5722"
            : Boolean(type?.includes("anno-shape") && feature)
            ? "rgba(0, 0, 0, 0.26)"
            : "rgba(0, 0, 0, 1)",
          fill: "none",
        }}
      />
    );
  };

  const changeColor = (e: any) => {
    const mapEvent: any = map;
    const draw = mapEvent._controls.find((control: any) => control.modes);
    if (feature && colorpickEl?.name === "fill") {
      draw.setFeatureProperty(
        feature.id,
        "fill-color",
        `rgb(${e.rgb.r},${e.rgb.g},${e.rgb.b})`
      );
      draw.setFeatureProperty(feature.id, "fill-opacity", e.rgb.a);
      setevent({
        ...event,
        feature: {
          ...event.feature,
          properties: {
            ...event.feature?.properties,
            "fill-color": `rgb(${e.rgb.r},${e.rgb.g},${e.rgb.b})`,
            "fill-opacity": e.rgb.a,
          },
        },
      });
    } else if (feature && colorpickEl?.name === "stroke") {
      draw.setFeatureProperty(
        feature.id,
        "fill-outline-color",
        `rgb(${e.rgb.r},${e.rgb.g},${e.rgb.b})`
      );
      setevent({
        ...event,
        feature: {
          ...event.feature,
          properties: {
            ...event.feature?.properties,
            "fill-outline-color": `rgb(${e.rgb.r},${e.rgb.g},${e.rgb.b})`,
          },
        },
      });
    }
  };

  const renderColorPicker = (el: any) => {
    let outputColor = "#000000";
    if (el?.name === "fill") {
      outputColor = feature?.properties?.["fill-color"];
    } else {
      outputColor = feature?.properties?.["fill-outline-color"];
    }

    return outputColor;
  };

  const changeShape = (s: IShape["shape"]) => () => {
    setshapepick(s);
    setshapepickEl(null);
  };

  return (
    <Fragment>
      <Popover
        open={Boolean(shapepickEl)}
        anchorEl={shapepickEl}
        onClose={() => {
          setshapepickEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <List component="nav" aria-label="main mailbox folders">
          {shapeList.map((shape: any) => (
            <ListItem key={shape} button onClick={changeShape(shape)}>
              <ListItemIcon>{renderShape(shape)}</ListItemIcon>
              <ListItemText primary={shape} />
            </ListItem>
          ))}
        </List>
      </Popover>
      <Popover
        open={Boolean(colorpickEl)}
        anchorEl={colorpickEl}
        onClose={() => {
          setcolorpickEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <ChromePicker
          color={renderColorPicker(colorpickEl)}
          onChange={changeColor}
        />
      </Popover>
      <Typography variant="subtitle2" className={clsx(classes.flexMiddle)}>
        Drawing:
      </Typography>
      <ButtonGroup className={classes.spaceInGroup}>
        <Button
          variant="outlined"
          size="small"
          color={Boolean(type && !feature) ? "primary" : "default"}
          className={clsx(classes.btnSelect)}
          onClick={() => {
            setevent({ ...event, type: shapepick });
          }}
          disabled={Boolean(type && feature)}
        >
          {renderShape(shapepick)}
        </Button>
        <Button
          variant="outlined"
          size="small"
          className={clsx(classes.btnSelect)}
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setshapepickEl(event.currentTarget);
          }}
          disabled={Boolean(type && feature)}
        >
          <ArrowDropDown
            className={clsx(!Boolean(type && feature) && classes.default)}
          />
        </Button>
      </ButtonGroup>
      <ButtonGroup className={classes.spaceInGroup}>
        <Button
          variant="outlined"
          size="small"
          className={clsx(classes.btnSelect)}
          disabled={!Boolean(type?.includes("anno-shape") && feature)}
        >
          <RectangleDefault
            className={clsx(
              Boolean(type?.includes("anno-shape") && feature) &&
                classes.default
            )}
            style={
              type?.includes("anno-shape") && feature
                ? { fill: feature.properties?.["fill-color"] }
                : {}
            }
          />
        </Button>
        <Button
          variant="outlined"
          size="small"
          className={clsx(classes.btnSelect)}
          name="fill"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setcolorpickEl(event.currentTarget);
          }}
          disabled={!Boolean(type?.includes("anno-shape") && feature)}
        >
          <ArrowDropDown
            className={clsx(
              Boolean(type?.includes("anno-shape") && feature) &&
                classes.default
            )}
          />
        </Button>
      </ButtonGroup>
      <ButtonGroup className={classes.spaceInGroup}>
        <Button
          variant="outlined"
          size="small"
          className={clsx(classes.btnSelect)}
          disabled={!Boolean(type?.includes("anno-shape") && feature)}
        >
          <RectangleDefault
            className={clsx(
              Boolean(type?.includes("anno-shape") && feature) &&
                classes.default
            )}
            style={
              type?.includes("anno-shape") && feature
                ? {
                    fill: "none",
                    strokeWidth: 4,
                    stroke: feature.properties?.["fill-outline-color"],
                  }
                : {
                    fill: "none",
                    strokeWidth: 4,
                    stroke: "rgba(0, 0, 0, 0.26)",
                  }
            }
          />
        </Button>
        <Button
          variant="outlined"
          size="small"
          className={clsx(classes.btnSelect)}
          name="stroke"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setcolorpickEl(event.currentTarget);
          }}
          disabled={!Boolean(type?.includes("anno-shape") && feature)}
        >
          <ArrowDropDown
            className={clsx(
              Boolean(type?.includes("anno-shape") && feature) &&
                classes.default
            )}
          />
        </Button>
      </ButtonGroup>
    </Fragment>
  );
};

export default DrawingShape;
